import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera } from 'lucide-react';
import { useApplications } from '../hooks/usePortal';
import { usePortalContext } from '../hooks/usePortalContext';
import { api, auth } from '../lib/api';
import { isValidProfilePhone, normalizePhone } from '../lib/phone';
import { humanize, uploadProfilePhoto } from '../lib/portal';
import Avatar from '../components/shell/Avatar';
import InlineError from '../components/InlineError';
import PasswordInput from '../components/PasswordInput';
import CopyableValue from '../components/CopyableValue';

/**
 * Self-service password reset: collapsed "Reset Password" button that reveals
 * a new/confirm form and PUTs to the bearer-authenticated /auth/password.
 */
function PasswordCard() {
  const [open, setOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await api('/auth/password', {
        method: 'PUT',
        body: JSON.stringify({ newPassword, confirmPassword }),
      });
      setNewPassword('');
      setConfirmPassword('');
      setOpen(false);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update your password right now.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>Password</h2>
      {!open ? (
        <>
          {done && <p role="status">Your password has been updated.</p>}
          <p style={{ marginTop: 12 }}>
            <button onClick={() => { setDone(false); setOpen(true); }} style={{ width: 'auto' }}>
              Reset Password
            </button>
          </p>
        </>
      ) : (
        <form onSubmit={onSubmit}>
          <label>
            New password
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="At least 8 characters"
            />
          </label>
          <label>
            Confirm new password
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="Re-enter your new password"
            />
          </label>
          {error && <InlineError message={error} />}
          <p style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button type="submit" disabled={busy || !newPassword || !confirmPassword} style={{ width: 'auto' }}>
              {busy ? 'Updating…' : 'Update password'}
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); setError(null); setNewPassword(''); setConfirmPassword(''); }}
              disabled={busy}
              style={{ width: 'auto' }}
            >
              Cancel
            </button>
          </p>
        </form>
      )}
    </div>
  );
}

/**
 * Preferred name (Path 2 — live account update): a non-legal display name the
 * applicant owns directly, alongside the Personal Information step entry
 * point. Legal first/last name stay staff-managed — same allowlist boundary
 * as ContactDetailsCard below.
 */
function PreferredNameCard() {
  const { ctx, reload } = usePortalContext();
  const current = ctx?.preferredName ?? '';
  const [preferredName, setPreferredName] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => setPreferredName(current), [current]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const trimmed = preferredName.trim();
    setBusy(true);
    try {
      await api('/me/profile', {
        method: 'PATCH',
        body: JSON.stringify({ preferredName: trimmed.length > 0 ? trimmed : null }),
      });
      reload();
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update your preferred name right now.');
    } finally {
      setBusy(false);
    }
  }

  const unchanged = preferredName.trim() === (current ?? '').trim();
  return (
    <form className="card" onSubmit={onSubmit}>
      <h2 style={{ marginTop: 0, marginBottom: 2 }}>Preferred name</h2>
      <p className="muted" style={{ marginBottom: 16 }}>
        What we call you instead of your legal first name — optional, shown throughout the portal.
      </p>
      <label>
        Preferred name
        <input
          type="text"
          value={preferredName}
          onChange={(e) => {
            setPreferredName(e.target.value);
            setSaved(false);
          }}
          maxLength={100}
          placeholder="e.g. JP"
        />
      </label>
      {error && <InlineError message={error} />}
      {saved && !error && <p role="status">Your preferred name has been updated.</p>}
      <p style={{ marginTop: 12 }}>
        <button type="submit" disabled={busy || unchanged} style={{ width: 'auto' }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </p>
    </form>
  );
}

/**
 * Contact details (Path 2 — live account update): phone is the ONLY field
 * the applicant can edit here (BFF allowlist in front of the Hub's strict
 * 4-field PATCH; name/email are managed by Alliance Abroad staff, the photo
 * has its own camera control above). Saves are EXPLICIT — never on blur:
 * every successful save fires a Hub audit row, a staff touch-signal, rule
 * re-evaluation and an applicant.updated event to HubSpot/n8n, so the button
 * is the applicant's deliberate "yes, publish this".
 */
function ContactDetailsCard() {
  const { ctx, reload } = usePortalContext();
  const current = ctx?.phone ?? '';
  const [phone, setPhone] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  // Re-seed the input whenever the context (re)resolves — initial load and
  // the post-save reload() both land here with the Hub-canonical value.
  useEffect(() => setPhone(current), [current]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const next = normalizePhone(phone);
    // Pre-validate strict E.164 (the Hub hard-400s malformed phones). Bare
    // national numbers are rejected — even 10-digit NANP ones the Hub would
    // canonicalise to +1 — because a foreign 10-digit number would be
    // silently mis-filed as US (see lib/phone.ts).
    if (!isValidProfilePhone(next)) {
      setError('Enter a valid phone number — include your country code, e.g. +27 82 123 4567.');
      return;
    }
    setBusy(true);
    try {
      await api('/me/profile', { method: 'PATCH', body: JSON.stringify({ phone: next }) });
      // Refresh /me/context so the displayed value is the Hub's canonical one.
      reload();
      setSaved(true);
    } catch (err) {
      // The BFF passes the Hub's verdict through verbatim (e.g. its E.164
      // rejection message) — render it as-is.
      setError(err instanceof Error ? err.message : 'Could not update your phone number right now.');
    } finally {
      setBusy(false);
    }
  }

  // Normalize BOTH sides: the stored value may carry legacy formatting (e.g.
  // "+27 821234567" written by a Path-1 step submission), and comparing it
  // raw against the normalized input would enable Save with zero edits — a
  // click then fires a REAL upstream change (audit row + HubSpot/n8n event).
  const unchanged = normalizePhone(phone) === normalizePhone(current ?? '');
  return (
    <form className="card" onSubmit={onSubmit}>
      <h2 style={{ marginTop: 0, marginBottom: 2 }}>Contact details</h2>
      <p className="muted" style={{ marginBottom: 16 }}>
        Your name and email are managed by Alliance Abroad — contact your coordinator to change them.
      </p>
      <dl className="proplist">
        <dt>Phone</dt><dd>{current || '—'}</dd>
      </dl>
      <label>
        Phone number
        <input
          type="tel"
          value={phone}
          onChange={(e) => {
            setPhone(e.target.value);
            // A stale "updated" banner over an edited input reads as "this
            // new value is saved" — clear it as soon as the value diverges.
            setSaved(false);
          }}
          autoComplete="tel"
          placeholder="Include your country code, e.g. +27 82 123 4567"
        />
      </label>
      {error && <InlineError message={error} />}
      {saved && !error && <p role="status">Your phone number has been updated.</p>}
      <p style={{ marginTop: 12 }}>
        <button type="submit" disabled={busy || !phone.trim() || unchanged} style={{ width: 'auto' }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </p>
    </form>
  );
}

/** Profile: live applicant + program details from the active application. */
export default function ProfilePage() {
  const { active, apps, loading, error } = useApplications();
  const { ctx, reload } = usePortalContext();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  // Path 2: change the account photo directly. On success we reload /me/context
  // so every avatar surface (sidebar, header menu, drawer) updates at once.
  async function onPickPhoto(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the applicant re-pick the same file after an error
    if (!file) return;
    setPhotoError(null);
    setBusy(true);
    try {
      await uploadProfilePhoto(file);
      reload();
    } catch (err) {
      setPhotoError(err instanceof Error ? err.message : 'Could not update your photo right now.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="card">Loading profile…</div>;
  if (error) return <div className="card" role="alert"><p>{error}</p></div>;

  const first = ctx?.preferredName ?? active?.preferredName ?? ctx?.firstName ?? active?.firstName ?? '';
  const last = ctx?.lastName ?? active?.lastName ?? '';
  const name = `${first} ${last}`.trim() || 'Your account';
  const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || 'AA';
  const email = ctx?.email ?? active?.email;

  return (
    <>
      <div className="card profilehero">
        <div className="avataredit xl">
          <Avatar user={{ name, initials, email, avatarUrl: ctx?.profileImageUrl ?? undefined }} className="xl" />
          <button
            type="button"
            className="avataredit-btn"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            aria-label="Change profile photo"
          >
            <Camera size={16} aria-hidden />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png"
            style={{ display: 'none' }}
            onChange={onPickPhoto}
          />
        </div>
        <h2 className="profilehero-name">{name}</h2>
        {email && <p className="profilehero-email">{email}</p>}
        <p className="muted">{busy ? 'Uploading photo…' : 'Tap the camera to change your photo'}</p>
        {photoError && <InlineError message={photoError} style={{ marginTop: 6 }} />}
      </div>
      <div className="card">
        <h2 style={{ marginTop: 0, marginBottom: 2 }}>Profile Information</h2>
        <p className="muted" style={{ marginBottom: 16 }}>Your program and application details</p>
        <dl className="proplist">
          <dt>Email</dt><dd><CopyableValue value={email} label="Email address" testId="profile-email" /></dd>
          <dt>Program</dt><dd>{ctx?.program?.name ?? active?.programName ?? '—'}</dd>
          <dt>Brand</dt><dd>{ctx?.brand?.name ?? active?.brandName ?? '—'}</dd>
          {/* Journey stage, NEVER the internal readinessState (`status`) — an
              operational triage key like "blocked" must not reach applicants.
              Fallback humanizes the raw journey key for older Globus payloads
              that don't send the label yet. */}
          <dt>Application status</dt>
          <dd>
            {active
              ? active.journeyPositionLabel ?? (active.journeyPosition ? humanize(active.journeyPosition) : 'In progress')
              : 'No application yet'}
          </dd>
          {active && (
            <>
              <dt>Application ID</dt><dd><CopyableValue value={active.shortCode ?? String(active.id)} label="Application ID" testId="profile-application-id" /></dd>
            </>
          )}
          <dt>Applications</dt><dd>{apps.length}</dd>
        </dl>
      </div>
      <PreferredNameCard />
      <ContactDetailsCard />
      <PasswordCard />
      <div className="card">
        <h2>Session</h2>
        <p>Signed in with your Alliance Abroad account.</p>
        <p style={{ marginTop: 12 }}>
          <button onClick={() => { auth.clear(); navigate('/sign-in'); }} style={{ width: 'auto' }}>Sign out</button>
        </p>
      </div>
    </>
  );
}
